import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const ImageSlider = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);
  
  const imageArray = Array.isArray(images) ? images : [images];
  
  const styles = {
    sliderContainer: {
      position: 'relative',
      width: '100%',
      height: '250px',
      overflow: 'hidden'
    },
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      transition: 'opacity 0.3s ease'
    },
    dots: {
      position: 'absolute',
      bottom: '10px',
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      gap: '6px',
      zIndex: '2'
    },
    dot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: '#ccc',
      cursor: 'pointer',
      transition: 'background-color 0.3s ease'
    },
    activeDot: {
      backgroundColor: '#400684'
    }
  };

  const startAutoSlide = () => {
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % imageArray.length);
    }, 2000);
  };

  const stopAutoSlide = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Clear interval on component unmount
  useEffect(() => {
    return () => stopAutoSlide();
  }, []);

  return (
    <div
      style={styles.sliderContainer}
      onMouseEnter={startAutoSlide}
      onMouseLeave={stopAutoSlide}
    >
      <img
        src={imageArray[currentIndex]}
        alt={`Slide ${currentIndex + 1}`}
        style={styles.image}
      />
      
      {imageArray.length > 1 && (
        <div style={styles.dots}>
          {imageArray.map((_, idx) => (
            <div
              key={idx}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(idx);
              }}
              style={{
                ...styles.dot,
                ...(currentIndex === idx ? styles.activeDot : {})
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CompareBar = ({ selectedProducts, onRemoveProduct }) => {
    const navigate = useNavigate();
    
    // Responsive breakpoints
    const breakpoints = {
        mobile: 480,
        tablet: 768,
        desktop: 1024
    };

    // Dynamic responsive styles
    const getResponsiveStyle = (screenWidth) => ({
        compareBar: {
            position: 'fixed',
            bottom: '0',
            left: '0',
            right: '0',
            backgroundColor: '#fff',
            boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
            padding: screenWidth < breakpoints.mobile 
                ? '10px 5px' 
                : screenWidth < breakpoints.tablet 
                    ? '12px 10px' 
                    : '15px 10px',
            zIndex: '1000',
            display: selectedProducts.length > 0 ? 'block' : 'none',
            transition: 'all 0.3s ease'
        },
        compareContent: {
            maxWidth: '1200px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: screenWidth < breakpoints.tablet ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 15px',
            gap: screenWidth < breakpoints.mobile ? '10px' : '20px'
        },
        compareText: {
            color: '#410684',
            fontFamily: "Oswald, sans-serif",
            fontSize: screenWidth < breakpoints.mobile 
                ? '14px' 
                : screenWidth < breakpoints.tablet 
                    ? '15px' 
                    : '20px',
            fontWeight: '600',
            marginBottom: screenWidth < breakpoints.tablet ? '10px' : '0'
        },
        productsContainer: {
            display: 'flex',
          
            flexWrap: screenWidth < breakpoints.tablet ? 'wrap' : 'nowrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: screenWidth < breakpoints.mobile ? '8px' : '12px',
            flex: '1',
            maxWidth: '100%'
        },
        productPill: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: screenWidth < breakpoints.mobile 
                ? '6px 10px' 
                : '8px 12px',
                backgroundColor:'#410684',
            borderRadius: '20px',
            border: '1px solid #ddd',
            maxWidth: screenWidth < breakpoints.mobile ? '120px' : '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            transition: 'all 0.3s ease'
        },
        removeButton: {
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '2px',
            color: '#666',
            fontSize: screenWidth < breakpoints.mobile ? '14px' : '16px',
            transition: 'color 0.3s ease'
        },
        compareButton: {
            padding: screenWidth < breakpoints.mobile 
                ? '8px 15px' 
                : screenWidth < breakpoints.tablet 
                    ? '9px 18px' 
                    : '10px 20px',
            backgroundColor: '#400684',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            fontWeight: '500',
            width: screenWidth < breakpoints.tablet ? '100%' : 'auto',
            marginTop: screenWidth < breakpoints.tablet ? '10px' : '0',
            marginLeft: screenWidth < breakpoints.tablet ? '0' : '20px',
            marginRight: screenWidth < breakpoints.tablet ? '0' : '40px',
            transition: 'all 0.3s ease',
            transform: 'scale(1)',
            '&:hover': {
                transform: 'scale(1.05)',
                backgroundColor: '#5a0bb5'
            }
        },
        emptySlot: {
            padding: screenWidth < breakpoints.mobile 
                ? '6px 10px' 
                : '8px 12px',
            backgroundColor: '#f5f5f5',
            borderRadius: '20px',
            border: '1px dashed #ddd',
            color: '#999',
            minWidth: screenWidth < breakpoints.mobile ? '100px' : '120px',
            textAlign: 'center',
            maxWidth: screenWidth < breakpoints.mobile ? '120px' : '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            transition: 'all 0.3s ease'
        }
    });

    // Media query state management
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    
    useEffect(() => {
        const handleResize = () => setScreenWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Create slots for products
    const slots = Array(4).fill(null);
    selectedProducts.forEach((product, index) => {
        slots[index] = product;
    });

    // Style based on current screen width
    const styles = getResponsiveStyle(screenWidth);

    const handleCompareClick = () => {
        if (selectedProducts.length >= 2) {
            navigate('/compare', { state: { products: selectedProducts } });
        }
    };

    return (
        <div style={styles.compareBar}>
            <div style={styles.compareContent}>
                <span style={styles.compareText}>Compare</span>
                <div style={styles.productsContainer}>
                    {slots.map((product, index) => (
                        product ? (
                            <div 
                                key={product.id} 
                                style={styles.productPill}
                            >
                                <span style={{ 
                                    whiteSpace: 'nowrap', 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis',
                                    maxWidth: screenWidth < breakpoints.mobile ? '80px' : '150px'
                                }}>
                                    {product.name}
                                </span>
                                <button 
                                    style={styles.removeButton}
                                    onClick={() => onRemoveProduct(product.id)}
                                    aria-label="Remove product"
                                >
                                    ×
                                </button>
                            </div>
                        ) : (
                            <div 
                                key={index} 
                                style={styles.emptySlot}
                            >
                                Empty slot {index + 1}
                            </div>
                        )
                    ))}
                </div>
                <button 
                    style={{
                        ...styles.compareButton,
                        opacity: selectedProducts.length >= 2 ? '1' : '0.5',
                        cursor: selectedProducts.length >= 2 ? 'pointer' : 'not-allowed'
                    }}
                    onClick={handleCompareClick}
                    disabled={selectedProducts.length < 2}
                >
                    {selectedProducts.length >= 2 ? 'COMPARE NOW' : 'SELECT ANOTHER TO COMPARE'}
                </button>
            </div>
        </div>
    );
};
const ProductGrid = () => {
  const [selectedProducts, setSelectedProducts] = useState([]);

  const products = [{
    id: "RUT956",
    name: "RUT956",
    images: ["/rut956.png", "/rut956-1.webp", "/rut956-6.webp", "/rut956-7.webp"],
    features: ["4G LTE (Cat 4)", "3G, 2G", "Wi-Fi Multiple Physical Interface"],
    specs: {
        technology: {
            cellular: {
                "4G LTE": {
                    value: true,
                    label: "CAT 4"
                },
                "3G": {
                    value: true,
                    label: "✓"
                },
                "2G": {
                    value: true,
                    label: "✓"
                },
                "Wi-Fi": {
                    value: true,
                    label: "Dual-Band (b/g/n/ac)"
                }
            },
            connectivity: {
                SMS: {
                    value: true,
                    label: "✓"
                },
                ethernet: {
                    value: true,
                    label: "10/100/1000"
                }
            },
            features: {
                eSIM: {
                    value: !1,
                    label: "-"
                },
                TPM: {
                    value: !1,
                    label: "-"
                },
                "5G": {
                    value: !1,
                    label: "-"
                },
                GNSS: {
                    value: true,
                    label: "✓"
                }
            }
        },
        hardware: {
            ram: {
                value: "128 MB",
                label: "128 MB"
            },
            flash_memory: {
                value: "16 MB",
                label: "16 MB"
            },
            io_connector: {
                value: "4-pin",
                label: "4-pin,Terminal Block"
            },
            ethernet_ports: {
                value: "2",
                label: "4"
            },
            inputs_outputs: {
                value: "Passive PoE In",
                label: "7"
            },
            passive_poe_in: {
                value: "Passive PoE In",
                label: "✓"
            },
            power_led: {
                value: "1",
                label: "1"
            },
            mobile_antenna: {
                value: "2x SMA",
                label: "2x SMA"
            },
            wifi_antenna: {
                value: "1x RP-SMA",
                label: "2x RP-SMA"
            },
            sim_card_slots: {
                value: "1x 2FF",
                label: "2x 2FF"
            },
            power_consumption: {
                value: "< 2 W idle, < 6.5 W Max",
                label: "< 2W idle, < 7W Max"
            },
            mounting_options: {
                value: "DIN rail, wall mount, flat surface (all require additional kit)",
                label: "DIN rail, wall mount, flat surface (all require additional kit)"
            },
            dimensions: {
                value: "83 x 25 x 74 mm",
                label: "110 x 50 x 100 mm"
            },
            housing_material: {
                value: "Aluminium/plastic",
                label: "Aluminium/plastic"
            },
            cpu: {
                value: "580 MHz",
                label: "580 MHz"
            },
            power_socket: {
                value: "4-pin",
                label: "4-pin"
            },
            supply_voltage: {
                value: "9 \u2013 30 VDC",
                label: "9 \u2013 30 VDC"
            },
            operating_temperature: {
                value: "-40 \xb0C to 75 \xb0C",
                label: "-40 \xb0C to 75 \xb0C"
            },
            gpp_release: {
                value: "Release 10/11 depending on the hardware version",
                label: "Release 11"
            },
            gps_antenna: {
                value: "1x SMA",
                label: "1x SMA"
            },
            usb: {
                value: "-",
                label: "✓"
            },
            rs232: {
                value: "-",
                label: "✓"
            },
            rs485: {
                value: "-",
                label: "✓"
            }
        },
        firmware: {
            modbus_tcp_client: {
                value: true,
                label: "✓"
            },
            ntp_client_ntp_server: {
                value: true,
                label: "✓"
            },
            input_events: {
                value: true,
                label: "✓"
            },
            wake_on_lan: {
                value: true,
                label: "✓"
            },
            manual_band_lock: {
                value: true,
                label: "✓"
            },
            modbus_tcp_server: {
                value: true,
                label: "✓"
            },
            mqtt_gateway: {
                value: true,
                label: "✓"
            },
            ussd: {
                value: true,
                label: "✓"
            },
            dnp3_station: {
                value: true,
                label: "✓"
            },
            dnp3_outstation: {
                value: true,
                label: "✓"
            },
            sstp: {
                value: true,
                label: "✓"
            },
            stunnel: {
                value: true,
                label: "✓"
            },
            management_via_sms: {
                value: true,
                label: "✓"
            },
            bridge_mode: {
                value: true,
                label: "✓"
            },
            manual_operator_selection: {
                value: true,
                label: "✓"
            },
            dynamic_routing: {
                value: true,
                label: "✓"
            },
            hotspot: {
                value: true,
                label: "✓"
            },
            wireless_client: {
                value: true,
                label: "✓"
            },
            wireless_ap: {
                value: true,
                label: "✓"
            },
            ping_reboot: {
                value: true,
                label: "✓"
            },
            periodic_reboot: {
                value: true,
                label: "✓"
            },
            real_time_graphs: {
                value: true,
                label: "✓"
            },
            wan_failover: {
                value: true,
                label: "✓"
            },
            mobile_traffic_counter: {
                value: true,
                label: "✓"
            },
            connection_status_overview_bar: {
                value: true,
                label: "✓"
            },
            configuration_backup_restore: {
                value: true,
                label: "✓"
            },
            vrrp: {
                value: true,
                label: "✓"
            },
            ipsec: {
                value: true,
                label: "✓"
            },
            gre_tunnel: {
                value: true,
                label: "✓"
            },
            openvpn: {
                value: true,
                label: "✓"
            },
            pptp: {
                value: true,
                label: "✓"
            },
            l2tp: {
                value: true,
                label: "✓"
            },
            snmp: {
                value: true,
                label: "✓"
            },
            send_receive_sms: {
                value: true,
                label: "✓"
            },
            status_via_sms: {
                value: true,
                label: "✓"
            },
            sms_reboot: {
                value: true,
                label: "✓"
            },
            web_filter: {
                value: true,
                label: "✓"
            },
            mobile_data_limit: {
                value: true,
                label: "✓"
            },
            configuration_profiles: {
                value: true,
                label: "✓"
            },
            profiles_scheduler: {
                value: true,
                label: "✓"
            },
            event_logging: {
                value: true,
                label: "✓"
            },
            qos: {
                value: true,
                label: "✓"
            },
            fota: {
                value: true,
                label: "✓"
            },
            event_reporting: {
                value: true,
                label: "✓"
            },
            speed_test: {
                value: true,
                label: "✓"
            },
            disable_roaming: {
                value: true,
                label: "✓"
            },
            sms_gateway: {
                value: true,
                label: "✓"
            },
            ospf: {
                value: true,
                label: "✓"
            },
            access_control: {
                value: true,
                label: "✓"
            },
            vlan: {
                value: true,
                label: "✓"
            },
            cli: {
                value: true,
                label: "✓"
            },
            firewall: {
                value: true,
                label: "✓"
            },
            dynamic_dns: {
                value: true,
                label: "✓"
            },
            rms_support: {
                value: true,
                label: "✓"
            },
            dlms_support: {
                value: true,
                label: "✓"
            },
            rutos: {
                value: true,
                label: "RutOS"
            },
            modbus_gateway: {
                value: true,
                label: "✓"
            },
            geofencing: {
                value: true,
                label: "✓"
            },
            gps_data_upload_to_avl_server: {
                value: true,
                label: "✓"
            },
            samba_shares: {
                value: true,
                label: "✓"
            },
            printer_server: {
                value: true,
                label: "✓"
            },
            usb_to_serial: {
                value: true,
                label: "✓"
            },
            sim_switch: {
                value: true,
                label: "✓"
            },
            ntrip_client: {
                value: true,
                label: "✓"
            },
            modbus_rtu_master: {
                value: true,
                label: "✓"
            },
            dnp3_rtu_master: {
                value: true,
                label: "✓"
            },
            BACneT: {
                value: true,
                label: "✓"
            }
        }
    }
}, {
    id: "RUT241",
    name: "RUT241",
    images: ["/rut241.png", "/rut241-1.png", "/rut241-6.webp", "/rut241-3.png"],
    features: ["4G LTE (Cat 4)", "3G, 2G", "Wi-Fi", "WAN Failover"],
    specs: {
        technology: {
            cellular: {
                "4G LTE": {
                    value: true,
                    label: "CAT 4"
                },
                "3G": {
                    value: true,
                    label: "✓"
                },
                "2G": {
                    value: true,
                    label: "✓"
                },
                "Wi-Fi": {
                    value: true,
                    label: "2.4 GHz (b/g/n)"
                }
            },
            connectivity: {
                SMS: {
                    value: true,
                    label: "✓"
                },
                ethernet: {
                    value: true,
                    label: "10/100"
                }
            },
            features: {
                eSIM: {
                    value: !1,
                    label: "-"
                },
                TPM: {
                    value: !1,
                    label: "-"
                },
                "5G": {
                    value: !1,
                    label: "-"
                },
                GNSS: {
                    value: true,
                    label: "-"
                }
            }
        },
        hardware: {
            ram: {
                value: "128 MB",
                label: "128 MB"
            },
            flash_memory: {
                value: "16 MB",
                label: "16 MB"
            },
            io_connector: {
                value: "4-pin",
                label: "4-pin"
            },
            ethernet_ports: {
                value: "2",
                label: "2"
            },
            inputs_outputs: {
                value: "Passive PoE In",
                label: "2"
            },
            passive_poe_in: {
                value: "Passive PoE In",
                label: "✓"
            },
            power_led: {
                value: "1",
                label: "1"
            },
            mobile_antenna: {
                value: "2x SMA",
                label: "2x SMA"
            },
            wifi_antenna: {
                value: "1x RP-SMA",
                label: "1x RP-SMA"
            },
            sim_card_slots: {
                value: "1x 2FF",
                label: "1x 2FF"
            },
            power_consumption: {
                value: "< 2 W idle, < 6.5 W Max",
                label: "< 2 W idle, < 6.5 W Max"
            },
            mounting_options: {
                value: "DIN rail, wall mount, flat surface (all require additional kit)",
                label: "DIN rail, wall mount, flat surface (all require additional kit)"
            },
            dimensions: {
                value: "83 x 25 x 74 mm",
                label: "83 x 25 x 74 mm"
            },
            housing_material: {
                value: "Aluminium/plastic",
                label: "Aluminium/plastic"
            },
            cpu: {
                value: "580 MHz",
                label: "580 MHz"
            },
            power_socket: {
                value: "4-pin",
                label: "4-pin"
            },
            supply_voltage: {
                value: "9 \u2013 30 VDC",
                label: "9 \u2013 30 VDC"
            },
            operating_temperature: {
                value: "-40 \xb0C to 75 \xb0C",
                label: "-40 \xb0C to 75 \xb0C"
            },
            gpp_release: {
                value: "Release 10/11 depending on the hardware version",
                label: "Release 10/11 depending on the hardware version"
            },
            gps_antenna: {
                value: "1x SMA",
                label: "-"
            },
            usb: {
                value: "-",
                label: "-"
            },
            rs232: {
                value: "-",
                label: "-"
            },
            rs485: {
                value: "-",
                label: "-"
            }
        },
        firmware: {
            modbus_tcp_client: {
                value: true,
                label: "✓"
            },
            ntp_client_ntp_server: {
                value: true,
                label: "✓"
            },
            input_events: {
                value: true,
                label: "✓"
            },
            wake_on_lan: {
                value: true,
                label: "✓"
            },
            manual_band_lock: {
                value: true,
                label: "✓"
            },
            modbus_tcp_server: {
                value: true,
                label: "✓"
            },
            mqtt_gateway: {
                value: true,
                label: "✓"
            },
            ussd: {
                value: true,
                label: "✓"
            },
            dnp3_station: {
                value: true,
                label: "✓"
            },
            dnp3_outstation: {
                value: true,
                label: "✓"
            },
            sstp: {
                value: true,
                label: "✓"
            },
            stunnel: {
                value: true,
                label: "✓"
            },
            management_via_sms: {
                value: true,
                label: "✓"
            },
            bridge_mode: {
                value: true,
                label: "✓"
            },
            manual_operator_selection: {
                value: true,
                label: "✓"
            },
            dynamic_routing: {
                value: true,
                label: "✓"
            },
            hotspot: {
                value: true,
                label: "✓"
            },
            wireless_client: {
                value: true,
                label: "✓"
            },
            wireless_ap: {
                value: true,
                label: "✓"
            },
            ping_reboot: {
                value: true,
                label: "✓"
            },
            periodic_reboot: {
                value: true,
                label: "✓"
            },
            real_time_graphs: {
                value: true,
                label: "✓"
            },
            wan_failover: {
                value: true,
                label: "✓"
            },
            mobile_traffic_counter: {
                value: true,
                label: "✓"
            },
            connection_status_overview_bar: {
                value: true,
                label: "✓"
            },
            configuration_backup_restore: {
                value: true,
                label: "✓"
            },
            vrrp: {
                value: true,
                label: "✓"
            },
            ipsec: {
                value: true,
                label: "✓"
            },
            gre_tunnel: {
                value: true,
                label: "✓"
            },
            openvpn: {
                value: true,
                label: "✓"
            },
            pptp: {
                value: true,
                label: "✓"
            },
            l2tp: {
                value: true,
                label: "✓"
            },
            snmp: {
                value: true,
                label: "✓"
            },
            send_receive_sms: {
                value: true,
                label: "✓"
            },
            status_via_sms: {
                value: true,
                label: "✓"
            },
            sms_reboot: {
                value: true,
                label: "✓"
            },
            web_filter: {
                value: true,
                label: "✓"
            },
            mobile_data_limit: {
                value: true,
                label: "✓"
            },
            configuration_profiles: {
                value: true,
                label: "✓"
            },
            profiles_scheduler: {
                value: true,
                label: "✓"
            },
            event_logging: {
                value: true,
                label: "✓"
            },
            qos: {
                value: true,
                label: "✓"
            },
            fota: {
                value: true,
                label: "✓"
            },
            event_reporting: {
                value: true,
                label: "✓"
            },
            speed_test: {
                value: true,
                label: "✓"
            },
            disable_roaming: {
                value: true,
                label: "✓"
            },
            sms_gateway: {
                value: true,
                label: "✓"
            },
            ospf: {
                value: true,
                label: "✓"
            },
            access_control: {
                value: true,
                label: "✓"
            },
            vlan: {
                value: true,
                label: "✓"
            },
            cli: {
                value: true,
                label: "✓"
            },
            firewall: {
                value: true,
                label: "✓"
            },
            dynamic_dns: {
                value: true,
                label: "✓"
            },
            rms_support: {
                value: true,
                label: "✓"
            },
            dlms_support: {
                value: true,
                label: "✓"
            },
            rutos: {
                value: true,
                label: "RutOS"
            },
            modbus_gateway: {
                value: true,
                label: "-"
            },
            geofencing: {
                value: true,
                label: "-"
            },
            gps_data_upload_to_avl_server: {
                value: true,
                label: "-"
            },
            samba_shares: {
                value: true,
                label: "-"
            },
            printer_server: {
                value: true,
                label: "-"
            },
            usb_to_serial: {
                value: true,
                label: "-"
            },
            sim_switch: {
                value: true,
                label: "-"
            },
            ntrip_client: {
                value: true,
                label: "-"
            },
            modbus_rtu_master: {
                value: true,
                label: "-"
            },
            dnp3_rtu_master: {
                value: true,
                label: "-"
            },
            BACneT: {
                value: true,
                label: "-"
            }
        }
    }
}, {
    id: "RUTm50",
    name: "RUTm50",
    images: ["/rutm50.png", "/rutm50-1.webp", "/rutm50-2.webp", "/rutm50-3.webp"],
    features: ["5G/LTE (Cat 19)", "FCC-certified", "5 x Gigabit Ethernet"],
    specs: {
        technology: {
            cellular: {
                "4G LTE": {
                    value: true,
                    label: "CAT 19"
                },
                "3G": {
                    value: true,
                    label: "✓"
                },
                "2G": {
                    value: true,
                    label: "-"
                },
                "Wi-Fi": {
                    value: true,
                    label: "Dual-Band (b/g/n/ac)"
                }
            },
            connectivity: {
                SMS: {
                    value: true,
                    label: "✓"
                },
                ethernet: {
                    value: true,
                    label: "10/100/1000"
                }
            },
            features: {
                eSIM: {
                    value: !1,
                    label: "-"
                },
                TPM: {
                    value: !1,
                    label: "-"
                },
                "5G": {
                    value: !1,
                    label: "✓"
                },
                GNSS: {
                    value: true,
                    label: "✓"
                }
            }
        },
        hardware: {
            ram: {
                value: "128 MB",
                label: "256 MB"
            },
            flash_memory: {
                value: "16 MB",
                label: "256 MB"
            },
            io_connector: {
                value: "4-pin",
                label: "4-pin"
            },
            ethernet_ports: {
                value: "2",
                label: "5"
            },
            inputs_outputs: {
                value: "Passive PoE In",
                label: "2"
            },
            passive_poe_in: {
                value: "Passive PoE In",
                label: "✓"
            },
            power_led: {
                value: "1",
                label: "1"
            },
            mobile_antenna: {
                value: "2x SMA",
                label: "4x SMA"
            },
            wifi_antenna: {
                value: "1x RP-SMA",
                label: "2x RP-SMA"
            },
            sim_card_slots: {
                value: "1x 2FF",
                label: "2x 2FF"
            },
            power_consumption: {
                value: "< 2 W idle, < 6.5 W Max",
                label: "Idle: < 5 W / Max: < 18 W"
            },
            mounting_options: {
                value: "DIN rail, wall mount, flat surface (all require additional kit)",
                label: "DIN rail, wall mount, flat surface (all require additional kit)"
            },
            dimensions: {
                value: "83 x 25 x 74 mm",
                label: "132 x 44.2 x 95.1 mm"
            },
            housing_material: {
                value: "Aluminium/plastic",
                label: "Anodized aluminum housing and panels"
            },
            cpu: {
                value: "580 MHz",
                label: "880 MHz"
            },
            power_socket: {
                value: "4-pin",
                label: "4-pin"
            },
            supply_voltage: {
                value: "9 \u2013 30 VDC",
                label: "9 \u2013 50 VDC"
            },
            operating_temperature: {
                value: "-40 \xb0C to 75 \xb0C",
                label: "-40 \xb0C to 75 \xb0C"
            },
            gpp_release: {
                value: "Release 10/11 depending on the hardware version",
                label: "Release 16"
            },
            gps_antenna: {
                value: "1x SMA",
                label: "1x SMA"
            },
            usb: {
                value: "-",
                label: "✓"
            },
            rs232: {
                value: "-",
                label: "-"
            },
            rs485: {
                value: "-",
                label: "-"
            }
        },
        firmware: {
            modbus_tcp_client: {
                value: true,
                label: "✓"
            },
            ntp_client_ntp_server: {
                value: true,
                label: "✓"
            },
            input_events: {
                value: true,
                label: "✓"
            },
            wake_on_lan: {
                value: true,
                label: "✓"
            },
            manual_band_lock: {
                value: true,
                label: "✓"
            },
            modbus_tcp_server: {
                value: true,
                label: "✓"
            },
            mqtt_gateway: {
                value: true,
                label: "✓"
            },
            ussd: {
                value: true,
                label: "✓"
            },
            dnp3_station: {
                value: true,
                label: "✓"
            },
            dnp3_outstation: {
                value: true,
                label: "✓"
            },
            sstp: {
                value: true,
                label: "✓"
            },
            stunnel: {
                value: true,
                label: "✓"
            },
            management_via_sms: {
                value: true,
                label: "✓"
            },
            bridge_mode: {
                value: true,
                label: "✓"
            },
            manual_operator_selection: {
                value: true,
                label: "✓"
            },
            dynamic_routing: {
                value: true,
                label: "✓"
            },
            hotspot: {
                value: true,
                label: "✓"
            },
            wireless_client: {
                value: true,
                label: "✓"
            },
            wireless_ap: {
                value: true,
                label: "✓"
            },
            ping_reboot: {
                value: true,
                label: "✓"
            },
            periodic_reboot: {
                value: true,
                label: "✓"
            },
            real_time_graphs: {
                value: true,
                label: "✓"
            },
            wan_failover: {
                value: true,
                label: "✓"
            },
            mobile_traffic_counter: {
                value: true,
                label: "✓"
            },
            connection_status_overview_bar: {
                value: true,
                label: "✓"
            },
            configuration_backup_restore: {
                value: true,
                label: "✓"
            },
            vrrp: {
                value: true,
                label: "✓"
            },
            ipsec: {
                value: true,
                label: "✓"
            },
            gre_tunnel: {
                value: true,
                label: "✓"
            },
            openvpn: {
                value: true,
                label: "✓"
            },
            pptp: {
                value: true,
                label: "✓"
            },
            l2tp: {
                value: true,
                label: "✓"
            },
            snmp: {
                value: true,
                label: "✓"
            },
            send_receive_sms: {
                value: true,
                label: "✓"
            },
            status_via_sms: {
                value: true,
                label: "✓"
            },
            sms_reboot: {
                value: true,
                label: "✓"
            },
            web_filter: {
                value: true,
                label: "✓"
            },
            mobile_data_limit: {
                value: true,
                label: "✓"
            },
            configuration_profiles: {
                value: true,
                label: "✓"
            },
            profiles_scheduler: {
                value: true,
                label: "✓"
            },
            event_logging: {
                value: true,
                label: "✓"
            },
            qos: {
                value: true,
                label: "✓"
            },
            fota: {
                value: true,
                label: "✓"
            },
            event_reporting: {
                value: true,
                label: "✓"
            },
            speed_test: {
                value: true,
                label: "✓"
            },
            disable_roaming: {
                value: true,
                label: "✓"
            },
            sms_gateway: {
                value: true,
                label: "✓"
            },
            ospf: {
                value: true,
                label: "✓"
            },
            access_control: {
                value: true,
                label: "✓"
            },
            vlan: {
                value: true,
                label: "✓"
            },
            cli: {
                value: true,
                label: "✓"
            },
            firewall: {
                value: true,
                label: "✓"
            },
            dynamic_dns: {
                value: true,
                label: "✓"
            },
            rms_support: {
                value: true,
                label: "✓"
            },
            dlms_support: {
                value: true,
                label: "✓"
            },
            rutos: {
                value: true,
                label: "RutOS"
            },
            modbus_gateway: {
                value: true,
                label: "✓"
            },
            geofencing: {
                value: true,
                label: "✓"
            },
            gps_data_upload_to_avl_server: {
                value: true,
                label: "✓"
            },
            samba_shares: {
                value: true,
                label: "✓"
            },
            printer_server: {
                value: true,
                label: "✓"
            },
            usb_to_serial: {
                value: true,
                label: "✓"
            },
            sim_switch: {
                value: true,
                label: "✓"
            },
            ntrip_client: {
                value: true,
                label: "-"
            },
            modbus_rtu_master: {
                value: true,
                label: "-"
            },
            dnp3_rtu_master: {
                value: true,
                label: "-"
            },
            BACneT: {
                value: true,
                label: "-"
            }
        }
    }
}, {
    id: "RUT241_eSIM",
    name: "RUT241 eSIM\u2122",
    images: ["/rut241-esim.png", "/rut241-sim-1.png", "/rut241-sim-2.png", "/rut241-sim-4.png"],
    features: ["4G/LTE (Cat 4), 3G, 2G", "eSIM\u2122", "TPM"],
    specs: {
        technology: {
            cellular: {
                "4G LTE": {
                    value: true,
                    label: "CAT 4"
                },
                "3G": {
                    value: true,
                    label: "✓"
                },
                "2G": {
                    value: true,
                    label: "✓"
                },
                "Wi-Fi": {
                    value: true,
                    label: "2.4 GHz (b/g/n)"
                }
            },
            connectivity: {
                SMS: {
                    value: true,
                    label: "✓"
                },
                ethernet: {
                    value: true,
                    label: "10/100"
                }
            },
            features: {
                eSIM: {
                    value: !1,
                    label: "-"
                },
                TPM: {
                    value: !1,
                    label: "-"
                },
                "5G": {
                    value: !1,
                    label: "-"
                },
                GNSS: {
                    value: true,
                    label: "-"
                }
            }
        },
        hardware: {
            ram: {
                value: "128 MB",
                label: "128 MB"
            },
            flash_memory: {
                value: "16 MB",
                label: "16 MB"
            },
            io_connector: {
                value: "4-pin",
                label: "4-pin"
            },
            ethernet_ports: {
                value: "2",
                label: "2"
            },
            inputs_outputs: {
                value: "Passive PoE In",
                label: "2"
            },
            passive_poe_in: {
                value: "Passive PoE In",
                label: "✓"
            },
            power_led: {
                value: "1",
                label: "1"
            },
            mobile_antenna: {
                value: "2x SMA",
                label: "2x SMA"
            },
            wifi_antenna: {
                value: "1x RP-SMA",
                label: "1x RP-SMA"
            },
            sim_card_slots: {
                value: "1x 2FF",
                label: "1x 2FF"
            },
            power_consumption: {
                value: "< 2 W idle, < 6.5 W Max",
                label: "< 2 W idle, < 6.5 W Max"
            },
            mounting_options: {
                value: "DIN rail, wall mount, flat surface (all require additional kit)",
                label: "DIN rail, wall mount, flat surface (all require additional kit)"
            },
            dimensions: {
                value: "83 x 25 x 74 mm",
                label: "83 x 25 x 74 mm"
            },
            housing_material: {
                value: "Aluminium/plastic",
                label: "Aluminium/plastic"
            },
            cpu: {
                value: "580 MHz",
                label: "580 MHz"
            },
            power_socket: {
                value: "4-pin",
                label: "4-pin"
            },
            supply_voltage: {
                value: "9 \u2013 30 VDC",
                label: "9 \u2013 30 VDC"
            },
            operating_temperature: {
                value: "-40 \xb0C to 75 \xb0C",
                label: "-40 \xb0C to 75 \xb0C"
            },
            gpp_release: {
                value: "Release 10/11 depending on the hardware version",
                label: "Release 10/11 depending on the hardware version"
            },
            gps_antenna: {
                value: "1x SMA",
                label: "-"
            },
            usb: {
                value: "-",
                label: "-"
            },
            rs232: {
                value: "-",
                label: "-"
            },
            rs485: {
                value: "-",
                label: "-"
            }
        },
        firmware: {
            modbus_tcp_client: {
                value: true,
                label: "✓"
            },
            ntp_client_ntp_server: {
                value: true,
                label: "✓"
            },
            input_events: {
                value: true,
                label: "✓"
            },
            wake_on_lan: {
                value: true,
                label: "✓"
            },
            manual_band_lock: {
                value: true,
                label: "✓"
            },
            modbus_tcp_server: {
                value: true,
                label: "✓"
            },
            mqtt_gateway: {
                value: true,
                label: "✓"
            },
            ussd: {
                value: true,
                label: "✓"
            },
            dnp3_station: {
                value: true,
                label: "✓"
            },
            dnp3_outstation: {
                value: true,
                label: "✓"
            },
            sstp: {
                value: true,
                label: "✓"
            },
            stunnel: {
                value: true,
                label: "✓"
            },
            management_via_sms: {
                value: true,
                label: "✓"
            },
            bridge_mode: {
                value: true,
                label: "✓"
            },
            manual_operator_selection: {
                value: true,
                label: "✓"
            },
            dynamic_routing: {
                value: true,
                label: "✓"
            },
            hotspot: {
                value: true,
                label: "✓"
            },
            wireless_client: {
                value: true,
                label: "✓"
            },
            wireless_ap: {
                value: true,
                label: "✓"
            },
            ping_reboot: {
                value: true,
                label: "✓"
            },
            periodic_reboot: {
                value: true,
                label: "✓"
            },
            real_time_graphs: {
                value: true,
                label: "✓"
            },
            wan_failover: {
                value: true,
                label: "✓"
            },
            mobile_traffic_counter: {
                value: true,
                label: "✓"
            },
            connection_status_overview_bar: {
                value: true,
                label: "✓"
            },
            configuration_backup_restore: {
                value: true,
                label: "✓"
            },
            vrrp: {
                value: true,
                label: "✓"
            },
            ipsec: {
                value: true,
                label: "✓"
            },
            gre_tunnel: {
                value: true,
                label: "✓"
            },
            openvpn: {
                value: true,
                label: "✓"
            },
            pptp: {
                value: true,
                label: "✓"
            },
            l2tp: {
                value: true,
                label: "✓"
            },
            snmp: {
                value: true,
                label: "✓"
            },
            send_receive_sms: {
                value: true,
                label: "✓"
            },
            status_via_sms: {
                value: true,
                label: "✓"
            },
            sms_reboot: {
                value: true,
                label: "✓"
            },
            web_filter: {
                value: true,
                label: "✓"
            },
            mobile_data_limit: {
                value: true,
                label: "✓"
            },
            configuration_profiles: {
                value: true,
                label: "✓"
            },
            profiles_scheduler: {
                value: true,
                label: "✓"
            },
            event_logging: {
                value: true,
                label: "✓"
            },
            qos: {
                value: true,
                label: "✓"
            },
            fota: {
                value: true,
                label: "✓"
            },
            event_reporting: {
                value: true,
                label: "✓"
            },
            speed_test: {
                value: true,
                label: "✓"
            },
            disable_roaming: {
                value: true,
                label: "✓"
            },
            sms_gateway: {
                value: true,
                label: "✓"
            },
            ospf: {
                value: true,
                label: "✓"
            },
            access_control: {
                value: true,
                label: "✓"
            },
            vlan: {
                value: true,
                label: "✓"
            },
            cli: {
                value: true,
                label: "✓"
            },
            firewall: {
                value: true,
                label: "✓"
            },
            dynamic_dns: {
                value: true,
                label: "✓"
            },
            rms_support: {
                value: true,
                label: "✓"
            },
            dlms_support: {
                value: true,
                label: "✓"
            },
            rutos: {
                value: true,
                label: "RutOS"
            },
            modbus_gateway: {
                value: true,
                label: "-"
            },
            geofencing: {
                value: true,
                label: "-"
            },
            gps_data_upload_to_avl_server: {
                value: true,
                label: "-"
            },
            samba_shares: {
                value: true,
                label: "-"
            },
            printer_server: {
                value: true,
                label: "-"
            },
            usb_to_serial: {
                value: true,
                label: "-"
            },
            sim_switch: {
                value: true,
                label: "-"
            },
            ntrip_client: {
                value: true,
                label: "-"
            },
            modbus_rtu_master: {
                value: true,
                label: "-"
            },
            dnp3_rtu_master: {
                value: true,
                label: "-"
            },
            BACneT: {
                value: true,
                label: "-"
            }
        }
    }
}] ;

  const styles = {
    container: {
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto',
      marginBottom: selectedProducts.length > 0 ? '80px' : '20px'
    },
    // New heading styles
    headingContainer: {
      textAlign: 'center',
      marginBottom: '40px',
      position: 'relative'
    },
    heading: {
        fontSize: 'clamp(28px, calc(20px + 2.5vw), 50px)',
        fontWeight: '600',
        color: '#410684',
        lineHeight: 'clamp(30px, calc(25px + 1.5vw), 40px)',
        fontFamily: 'Oswald, sans-serif',
        margin: '0',
        position: 'relative',
        display: 'inline-block',
        '@media (max-width: 1200px)': {
          fontSize: '42px',
          lineHeight: '36px'
        },
        '@media (max-width: 768px)': {
          fontSize: '40px',
          lineHeight: '32px'
        },
        '@media (max-width: 480px)': {
          fontSize: '28px',
          lineHeight: '30px'
        }
      },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '20px'
    },
    card: {
      background: '#ffffff',
      borderRadius: '12px',
      padding: '20px',
      position: 'relative',
      minHeight: '430px',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)',
      transition: 'box-shadow 0.3s ease'
    },
    compareContainer: {
      position: 'absolute',
      top: '15px',
      right: '15px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      zIndex: '1',
      background: 'rgba(255, 255, 255, 0.9)',
      padding: '4px 8px',
      borderRadius: '4px'
    },
    checkbox: {
      width: '16px',
      height: '16px',
      cursor: 'pointer',
      accentColor: '#400684'
    },
    compareText: {
      color: '#414042',
      fontSize: '14px',
      fontFamily: 'Inter, sans-serif'
    },
    imageContainer: {
      flex: '1',
      marginBottom: '20px'
    },
    bottomSection: {
      marginTop: 'auto',
      display: 'flex',
      alignItems: 'stretch',
      gap: '20px',
      paddingTop: '20px'
    },
    productName: {
      fontSize: '32px',
      fontWeight: 'bold',
      color: '#400684',
      fontFamily: 'Oswald, sans-serif',
      margin: '0',
      flex: '0 0 auto',
      minWidth: '160px',
      paddingRight: '10px'
    },
    separator: {
      width: '2px',
      background: '#e0e0e0',
      margin: '0'
    },
    featureContainer: {
      flex: '1',
      paddingLeft: '10px'
    },
    featuresList: {
      listStyle: 'none',
      padding: '0',
      margin: '0',
      display: 'flex',
      flexDirection: 'column',
      gap: '5px'
    },
    feature: {
      fontSize: '14px',
      color: '#414042',
      fontFamily: 'Inter, sans-serif',
      lineHeight: '1.5'
    }
  };

  const handleCardClick = (productId) => {
    window.location.href = `/product/${productId}`;
  };

  const handleCompareToggle = (e, product) => {
    e.stopPropagation();
    setSelectedProducts(prev => {
      const isSelected = prev.some(p => p.id === product.id);
      if (isSelected) {
        return prev.filter(p => p.id !== product.id);
      } else if (prev.length < 4) {
        return [...prev, product];
      }
      return prev;
    });
  };

  const handleRemoveFromCompare = (productId) => {
    setSelectedProducts(prev => prev.filter(p => p.id !== productId));
  };

  return (
    <>
      <div style={styles.container}>
        <div style={styles.headingContainer}>
          <h1 style={styles.heading}>
            OUR PRODUCTS
            <div style={styles.headingUnderline}></div>
          </h1>
        </div>
        <div style={styles.grid}>
          {products.map((product) => (
            <div
              key={product.id}
              style={styles.card}
              onClick={() => handleCardClick(product.id)}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
              }}
            >
              <div 
                style={styles.compareContainer}
                onClick={(e) => e.stopPropagation()}
              >
                <input 
                  type="checkbox" 
                  style={styles.checkbox}
                  checked={selectedProducts.some(p => p.id === product.id)}
                  onChange={(e) => handleCompareToggle(e, product)}
                />
                <span style={styles.compareText}>Compare</span>
              </div>
              
              <div style={styles.imageContainer}>
                <ImageSlider images={product.images} />
              </div>

              <div style={styles.bottomSection}>
                <h2 style={styles.productName}>{product.name}</h2>
                <div style={styles.separator}></div>
                <div style={styles.featureContainer}>
                  <ul style={styles.featuresList}>
                    {product.features.map((feature, index) => (
                      <li key={index} style={styles.feature}>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <CompareBar 
        selectedProducts={selectedProducts}
        onRemoveProduct={handleRemoveFromCompare}
      />
    </>
  );
};

export default ProductGrid;